import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../services/api';

interface DropdownOption {
  value: string;
  display_name: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const dropdownStyles = {
  container: {
    position: 'relative' as const,
    width: '100%',
  },
  trigger: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  triggerDisabled: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    cursor: 'not-allowed',
  },
  triggerOpen: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  placeholder: {
    color: '#94a3b8',
  },
  arrow: {
    width: '1rem',
    height: '1rem',
    color: '#64748b',
    transition: 'transform 0.2s',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  option: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  optionHover: {
    backgroundColor: '#f1f5f9',
  },
  optionSelected: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
  },
};

const CustomDropdown: React.FC<CustomDropdownProps> = ({ options, value, onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={dropdownStyles.container}>
      <div
        ref={triggerRef}
        style={{
          ...dropdownStyles.trigger,
          ...(disabled ? dropdownStyles.triggerDisabled : {}),
          ...(isOpen ? dropdownStyles.triggerOpen : {}),
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={!selectedOption ? dropdownStyles.placeholder : undefined}>
          {selectedOption ? selectedOption.display_name : placeholder}
        </span>
        <svg
          style={{ ...dropdownStyles.arrow, ...(isOpen ? dropdownStyles.arrowOpen : {}) }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '0.375rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 9999,
          maxHeight: '12rem',
          overflowY: 'auto' as const,
        }}>
          {options.map((option, index) => (
            <div
              key={option.value}
              style={{
                ...dropdownStyles.option,
                ...(hoveredIndex === index ? dropdownStyles.optionHover : {}),
                ...(value === option.value ? dropdownStyles.optionSelected : {}),
              }}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {option.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface Client {
  id?: number;
  client_name: string;
  jump_client_id?: string;
  client_acronym?: string;
  client_type?: string;
  client_website?: string;
  contact1_name?: string;
  contact1_title?: string;
  contact1_email?: string;
  contact1_phone?: string;
  contact2_name?: string;
  contact2_title?: string;
  contact2_email?: string;
  contact2_phone?: string;
  contact3_name?: string;
  contact3_title?: string;
  contact3_email?: string;
  contact3_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  active: boolean;
}

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  mode: 'add' | 'edit';
  onSuccess?: () => void;
}

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.75rem',
    width: '100%',
    maxWidth: '70rem',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 1.5rem 3rem rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.75rem',
    color: '#64748b',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
    color: '#64748b',
    borderRadius: '0.375rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: '1.5rem',
    overflowY: 'auto' as const,
    flex: 1,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
    marginBottom: '1rem',
  },
  cardsRowThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  cardHeader: {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    padding: '0.625rem 1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  cardHeaderIcon: {
    width: '1rem',
    height: '1rem',
    color: '#3b82f6',
  },
  cardTitle: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#3b82f6',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    margin: 0,
  },
  cardBody: {
    padding: '1rem',
  },
  fieldGrid: {
    display: 'grid',
    gap: '0.875rem',
  },
  fieldGridTwoCol: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  label: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: '#64748b',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  inputDisabled: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    fontSize: '0.625rem',
    color: '#ef4444',
  },
  addressRow: {
    display: 'grid',
    gridTemplateColumns: '5.5rem 1fr 4rem',
    gap: '0.5rem',
  },
  toggle: {
    width: '2.5rem',
    height: '1.25rem',
    borderRadius: '0.625rem',
    cursor: 'pointer',
    position: 'relative' as const,
    transition: 'background-color 0.2s',
  },
  toggleOn: {
    backgroundColor: '#3b82f6',
  },
  toggleOff: {
    backgroundColor: '#cbd5e1',
  },
  toggleSlider: {
    width: '1rem',
    height: '1rem',
    backgroundColor: '#fff',
    borderRadius: '50%',
    position: 'absolute' as const,
    top: '0.125rem',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  toggleSliderOn: {
    left: '1.375rem',
  },
  toggleSliderOff: {
    left: '0.125rem',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    color: '#94a3b8',
    borderRadius: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  btnCancel: {
    padding: '0.5rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSave: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSaveDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  errorMessage: {
    color: '#dc2626',
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
  },
};

const ClientModal: React.FC<ClientModalProps> = ({
  isOpen,
  onClose,
  clientId,
  mode,
  onSuccess
}) => {
  const [formData, setFormData] = useState<Client>({
    client_name: '',
    jump_client_id: '',
    client_acronym: '',
    client_type: '',
    client_website: '',
    contact1_name: '',
    contact1_title: '',
    contact1_email: '',
    contact1_phone: '',
    contact2_name: '',
    contact2_title: '',
    contact2_email: '',
    contact2_phone: '',
    contact3_name: '',
    contact3_title: '',
    contact3_email: '',
    contact3_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    active: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [clientTypes, setClientTypes] = useState<Array<{value: string, display_name: string}>>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [originalFormData, setOriginalFormData] = useState<Client | null>(null);

  const isDirty = useMemo(() => {
    if (!originalFormData) return mode === 'add';
    const normalize = (val: unknown) => (val === null || val === undefined || val === '') ? '' : String(val);
    const keys = Object.keys(formData) as (keyof Client)[];
    return keys.some(key => normalize(formData[key]) !== normalize(originalFormData?.[key]));
  }, [formData, mode, originalFormData]);

  useEffect(() => {
    const fetchClientTypes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/lookup-values?category=client_type`, {
          headers: { ...auth.getAuthHeaders() }
        });
        if (response.ok) {
          setClientTypes(await response.json());
        }
      } catch (error) {
        console.error('Error fetching client types:', error);
      }
    };

    if (isOpen) {
      fetchClientTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchClient = async () => {
      setErrors({});
      setSuccessMessage('');
      setValidationErrors({});

      if (clientId && clientId > 0 && mode === 'edit') {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/clients/${clientId}`, {
            headers: { ...auth.getAuthHeaders() }
          });
          if (response.ok) {
            const client = await response.json();
            const contacts = client.contacts || [];
            const contact1 = contacts.find((c: any) => c.contact_type === 'primary') || {};
            const contact2 = contacts.find((c: any) => c.contact_type === 'secondary') || {};
            const contact3 = contacts.find((c: any) => c.contact_type === 'billing') || {};
            const addresses = client.addresses || [];
            const primaryAddress = addresses.find((a: any) => a.address_type === 'primary') || {};

            const clientData: Client = {
              id: client.id,
              client_name: client.client_name || '',
              jump_client_id: client.jump_client_id || '',
              client_acronym: client.client_acronym || '',
              client_type: client.client_type || '',
              client_website: client.client_website || '',
              active: client.active,
              contact1_name: contact1.contact_name || '',
              contact1_title: contact1.contact_title || '',
              contact1_email: contact1.contact_email || '',
              contact1_phone: formatPhoneNumber(contact1.contact_phone || ''),
              contact2_name: contact2.contact_name || '',
              contact2_title: contact2.contact_title || '',
              contact2_email: contact2.contact_email || '',
              contact2_phone: formatPhoneNumber(contact2.contact_phone || ''),
              contact3_name: contact3.contact_name || '',
              contact3_title: contact3.contact_title || '',
              contact3_email: contact3.contact_email || '',
              contact3_phone: formatPhoneNumber(contact3.contact_phone || ''),
              address_line1: primaryAddress.address_line1 || '',
              address_line2: primaryAddress.address_line2 || '',
              city: primaryAddress.city || '',
              state: primaryAddress.state || '',
              zip_code: primaryAddress.zip_code || ''
            };
            setFormData(clientData);
            setOriginalFormData({ ...clientData });
          }
        } catch (error) {
          console.error('Error fetching client:', error);
        }
      } else if (mode === 'add') {
        const emptyForm: Client = {
          client_name: '',
          jump_client_id: '',
          client_acronym: '',
          client_type: '',
          client_website: '',
          contact1_name: '',
          contact1_title: '',
          contact1_email: '',
          contact1_phone: '',
          contact2_name: '',
          contact2_title: '',
          contact2_email: '',
          contact2_phone: '',
          contact3_name: '',
          contact3_title: '',
          contact3_email: '',
          contact3_phone: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          zip_code: '',
          active: true
        };
        setFormData(emptyForm);
        setOriginalFormData(null);
      }
    };

    if (isOpen) {
      fetchClient();
    }
  }, [clientId, mode, isOpen]);

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const cleaned = value.replace(/\D/g, '');
    let formatted = value;
    if (cleaned.length === 10) {
      formatted = formatPhoneNumber(cleaned);
    } else if (cleaned.length <= 10) {
      formatted = cleaned;
    }
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleZipCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const zipCode = e.target.value;
    setFormData(prev => ({ ...prev, zip_code: zipCode }));

    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      } catch (error) {
        console.log('Could not auto-populate city/state for zip code:', zipCode);
      }
    }
  };

  const clearContact = (contactNumber: number) => {
    setFormData(prev => ({
      ...prev,
      [`contact${contactNumber}_name`]: '',
      [`contact${contactNumber}_title`]: '',
      [`contact${contactNumber}_email`]: '',
      [`contact${contactNumber}_phone`]: ''
    }));
  };

  const validateJumpClientId = async (value: string) => {
    if (!value || !value.trim()) {
      setValidationErrors(prev => ({ ...prev, jump_client_id: '' }));
      return;
    }

    try {
      const excludeParam = mode === 'edit' && clientId ? `?excludeClientId=${clientId}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/clients/validate-jump-client-id/${value}${excludeParam}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const result = await response.json();
        if (!result.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            jump_client_id: `Already assigned to "${result.existingClient.name}" - Reverting back`
          }));
          setTimeout(() => {
            setFormData(prev => ({
              ...prev,
              jump_client_id: originalFormData?.jump_client_id || ''
            }));
            setValidationErrors(prev => ({ ...prev, jump_client_id: '' }));
          }, 2000);
        } else {
          setValidationErrors(prev => ({ ...prev, jump_client_id: '' }));
        }
      }
    } catch (error) {
      console.error('Error validating JUMP client ID:', error);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.client_name || !formData.client_name.trim()) {
      newErrors.client_name = 'Client name is required';
    }

    if (formData.client_website && formData.client_website.trim()) {
      const website = formData.client_website.trim();
      const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/.*)?$/;
      if (!urlPattern.test(website)) {
        newErrors.client_website = 'Please enter a valid website URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const url = mode === 'add'
        ? `${process.env.REACT_APP_API_URL}/api/admin/clients`
        : `${process.env.REACT_APP_API_URL}/api/admin/clients/${clientId}`;

      const response = await fetch(url, {
        method: mode === 'add' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccessMessage('saved');
        onSuccess?.();
        setOriginalFormData({ ...formData });
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error('Failed to save client');
      }
    } catch (error) {
      console.error('Error saving client:', error);
      setErrors({ submit: 'Failed to save client. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const CardIcon = ({ type }: { type: string }) => {
    const iconProps = { style: styles.cardHeaderIcon, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' };

    switch (type) {
      case 'client':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'address':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'contact':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div
      style={{ ...styles.toggle, ...(checked ? styles.toggleOn : styles.toggleOff) }}
      onClick={onChange}
    >
      <div style={{ ...styles.toggleSlider, ...(checked ? styles.toggleSliderOn : styles.toggleSliderOff) }} />
    </div>
  );

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h2 style={styles.title}>{mode === 'add' ? 'Add New Client' : 'Edit Client'}</h2>
            {mode === 'edit' && (
              <div style={styles.statusToggle}>
                <span>Status:</span>
                <Toggle checked={formData.active || false} onChange={() => setFormData(prev => ({ ...prev, active: !prev.active }))} />
                <span>{formData.active ? 'Active' : 'Inactive'}</span>
              </div>
            )}
          </div>
          {successMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#dcfce7',
              color: '#16a34a',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Record Saved Successfully
            </div>
          )}
          <button style={styles.closeBtn} onClick={onClose} disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {errors.submit && <div style={styles.errorMessage}>{errors.submit}</div>}

            {/* Row 1: Client Information | Primary Address */}
            <div style={styles.cardsRow}>
              {/* Client Information Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <CardIcon type="client" />
                    <h3 style={styles.cardTitle}>Information</h3>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Client Name <span style={styles.required}>*</span></label>
                      <input
                        type="text"
                        name="client_name"
                        value={formData.client_name || ''}
                        onChange={handleInputChange}
                        style={{ ...styles.input, ...(errors.client_name ? styles.inputError : {}) }}
                        disabled={loading}
                      />
                      {errors.client_name && <span style={styles.errorText}>{errors.client_name}</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.875rem' }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Client Type</label>
                        <CustomDropdown
                          options={clientTypes}
                          value={formData.client_type || ''}
                          onChange={(val) => setFormData(prev => ({ ...prev, client_type: val }))}
                          placeholder="Select Type"
                          disabled={loading}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Client Acronym</label>
                        <input
                          type="text"
                          name="client_acronym"
                          value={formData.client_acronym || ''}
                          onChange={handleInputChange}
                          style={styles.input}
                          disabled={loading}
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.875rem' }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>JUMP Client ID</label>
                        <input
                          type="text"
                          name="jump_client_id"
                          value={formData.jump_client_id || ''}
                          onChange={handleInputChange}
                          onBlur={(e) => validateJumpClientId(e.target.value)}
                          style={{ ...styles.input, ...(validationErrors.jump_client_id ? styles.inputError : {}) }}
                          disabled={loading}
                        />
                        {validationErrors.jump_client_id && <span style={styles.errorText}>{validationErrors.jump_client_id}</span>}
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Website</label>
                        <input
                          type="text"
                          name="client_website"
                          value={formData.client_website || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...(errors.client_website ? styles.inputError : {}) }}
                          disabled={loading}
                          placeholder="www.example.com"
                        />
                        {errors.client_website && <span style={styles.errorText}>{errors.client_website}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary Address Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <CardIcon type="address" />
                    <h3 style={styles.cardTitle}>Primary Address</h3>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 1</label>
                      <input
                        type="text"
                        name="address_line1"
                        value={formData.address_line1 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 2</label>
                      <input
                        type="text"
                        name="address_line2"
                        value={formData.address_line2 || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                        placeholder="Apt, Suite, Unit, etc."
                      />
                    </div>
                    <div style={styles.addressRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>ZIP Code</label>
                        <input
                          type="text"
                          name="zip_code"
                          value={formData.zip_code || ''}
                          onChange={handleZipCodeChange}
                          style={styles.input}
                          disabled={loading}
                          maxLength={5}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state || ''}
                          onChange={handleInputChange}
                          style={{ ...styles.input, ...styles.inputDisabled }}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Contact Cards */}
            <div style={styles.cardsRowThree}>
              {/* Contact 1 - Primary */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <CardIcon type="contact" />
                    <h3 style={styles.cardTitle}>Primary Contact</h3>
                  </div>
                  <button type="button" style={styles.clearBtn} onClick={() => clearContact(1)} title="Clear contact">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><path d="M8 12h8"/>
                    </svg>
                  </button>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Name</label>
                      <input type="text" name="contact1_name" value={formData.contact1_name || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Title / Role</label>
                      <input type="text" name="contact1_title" value={formData.contact1_title || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Phone</label>
                        <input type="tel" name="contact1_phone" value={formData.contact1_phone || ''} onChange={handlePhoneChange} style={styles.input} disabled={loading} placeholder="(123) 456-7890" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email</label>
                        <input type="email" name="contact1_email" value={formData.contact1_email || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact 2 - Secondary */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <CardIcon type="contact" />
                    <h3 style={styles.cardTitle}>Secondary Contact</h3>
                  </div>
                  <button type="button" style={styles.clearBtn} onClick={() => clearContact(2)} title="Clear contact">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><path d="M8 12h8"/>
                    </svg>
                  </button>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Name</label>
                      <input type="text" name="contact2_name" value={formData.contact2_name || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Title / Role</label>
                      <input type="text" name="contact2_title" value={formData.contact2_title || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Phone</label>
                        <input type="tel" name="contact2_phone" value={formData.contact2_phone || ''} onChange={handlePhoneChange} style={styles.input} disabled={loading} placeholder="(123) 456-7890" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email</label>
                        <input type="email" name="contact2_email" value={formData.contact2_email || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact 3 - Billing */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <CardIcon type="contact" />
                    <h3 style={styles.cardTitle}>Billing Contact</h3>
                  </div>
                  <button type="button" style={styles.clearBtn} onClick={() => clearContact(3)} title="Clear contact">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><path d="M8 12h8"/>
                    </svg>
                  </button>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Name</label>
                      <input type="text" name="contact3_name" value={formData.contact3_name || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Title / Role</label>
                      <input type="text" name="contact3_title" value={formData.contact3_title || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Phone</label>
                        <input type="tel" name="contact3_phone" value={formData.contact3_phone || ''} onChange={handlePhoneChange} style={styles.input} disabled={loading} placeholder="(123) 456-7890" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email</label>
                        <input type="email" name="contact3_email" value={formData.contact3_email || ''} onChange={handleInputChange} style={styles.input} disabled={loading} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button type="button" style={styles.btnCancel} onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.btnSave,
                ...((loading || !isDirty || Object.values(validationErrors).some(err => err !== '')) ? styles.btnSaveDisabled : {})
              }}
              disabled={loading || !isDirty || Object.values(validationErrors).some(err => err !== '')}
            >
              {loading ? 'Saving...' : (mode === 'add' ? 'Create Client' : 'Update Client')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;
